import type { TCurrencyData } from "components/AdminPanel/types";
import { isPriceHistoryEntryConnectedToActivePeriod } from "components/Title/PriceHistory/utils";
import type { IChannel } from "containers/Channels/types";
import type { ITitle } from "containers/Titles/types";
import { applyVatToPrice } from "helpers/utils";
import type { TPeriod } from "store/periodStore";

import variables from "../../assets/styles/_variables.module.scss";
import type { IPriceHistoryEntry } from "../../store/priceHistory";
import type { Currencies } from "../../store/settingsStore";
import type { OverriddenChannel, TOverriddenChannelsData, TStartingChannelsData } from "./types";

/**
 * Compute the initial value of a price form field (in major units as a string) from a stored HT price in cents.
 *
 * Shared by the title-level price and the per-channel prices in `overriddenChannelsData`.
 * When VAT display is enabled AND `currency` matches `companyCurrency`, VAT is added for display
 * using `vatCountryCode` (the company country for the title, the channel's country market for a channel).
 * If `vatCountryCode` is empty/undefined or has no configured rate, no VAT is applied.
 */
export const getInitialPriceFormValue = ({
    usesPriceWithVat,
    price,
    currency,
    companyCurrency,
    vatCountryCode,
}: {
    usesPriceWithVat: boolean;
    price: number | string | undefined | null;
    currency: Currencies | TCurrencyData | string | undefined;
    companyCurrency: string | undefined;
    vatCountryCode: string;
}): string => {
    const numericPrice = Number(price ?? 0) ?? 0;
    const shouldApplyVat = usesPriceWithVat && currency === companyCurrency;
    const newPrice = shouldApplyVat ? applyVatToPrice(numericPrice, vatCountryCode) : numericPrice;
    return Intl.NumberFormat("en", { maximumFractionDigits: 2, minimumFractionDigits: 2 })
        .format(Math.round(newPrice) / 100)
        .replaceAll(",", "");
};

export type TChannelType = {
    isEUR: boolean;
    isFrance: boolean;
    isCountryMarketChannel: boolean;
};

/**
 * Verify channel condition, avoid duplicate code
 * @param channel pass overriden channel data
 * @param companyCurrency company base currency code
 * @returns {TChannelType}
 */
export const getChannelType = (channel: TOverriddenChannelsData, companyCurrency?: string) => {
    return {
        isEUR: channel?.currency === "EUR" || (channel.currency as TCurrencyData)?.code === "EUR",
        isCompanyCurrency:
            channel?.currency === companyCurrency || (channel.currency as TCurrencyData)?.code === companyCurrency,
        isFrance: channel.countryMarket === "FR",
        isCountryMarketChannel: !!channel.countryMarket,
    };
};

/**
 * Convert a quantity opening cumulative (count) to the display string used by the form.
 * Returns "" when there is no stored value.
 */
export const formatQuantityStartingCumulative = (value: number | string | null | undefined): string => {
    if (value == null || value === "") return "";
    const numericValue = +value;
    if (!numericValue) return "";
    return numericValue.toString();
};

/**
 * Convert a monetary opening cumulative (turnover / turnover_1 / turnover_2, stored in cents) to the
 * display string used by the form. Returns "" when there is no stored value.
 */
export const formatCustomTurnoverStartingCumulative = (value: number | string | null | undefined): string => {
    if (value == null || value === "") return "";
    const numericValue = +value;
    if (!numericValue) return "";
    return (numericValue / 100).toString();
};

/**
 * Convert a monetary opening cumulative from the form (display units, possibly comma decimals)
 * to cents as a string. Empty input becomes "0" so the API can clear the stored value.
 */
export const toCustomTurnoverCumulativeCents = (value: string | number | undefined): string => {
    if (value == null || value === "") return "0";
    return Math.round(+String(value).replace(",", ".") * 100).toString();
};

/**
 * Convert a quantity opening cumulative from the form to an integer string.
 * Empty input becomes "0" so the API can clear the stored value.
 */
export const toQuantityCumulative = (value: string | number | undefined): string => {
    if (value == null || value === "") return "0";
    return Math.round(+String(value).replace(",", ".")).toString();
};

export const formatStartingChannelsData = (
    channels: IChannel[],
    startingChannelsData?: ITitle["startingChannelsData"],
    editModeActive?: boolean,
) => {
    if (channels && channels.length > 0) {
        return channels
            .filter((channel) => channel.hasMilestones === 1)
            .map((item) => {
                const channelInfo = startingChannelsData?.find(
                    (channel: Omit<TStartingChannelsData, "usesAlternativeMilestoneAmount">) =>
                        channel.channelId === item.id,
                );
                if (channelInfo && editModeActive) {
                    return {
                        channelId: item.id,
                        name: item.name,
                        quantity: formatQuantityStartingCumulative(channelInfo.quantity),
                        turnover: formatCustomTurnoverStartingCumulative(channelInfo.turnover),
                        kind: channelInfo?.kind ?? undefined,
                        usesAlternativeMilestoneAmount: item.usesAlternativeMilestoneAmount ?? false,
                        turnover_1: formatCustomTurnoverStartingCumulative(channelInfo.turnover_1),
                        turnover_2: formatCustomTurnoverStartingCumulative(channelInfo.turnover_2),
                    };
                } else {
                    return {
                        channelId: item.id,
                        name: item.name,
                        quantity: "",
                        turnover: "",
                        kind: item.kind ?? undefined,
                        usesAlternativeMilestoneAmount: item.usesAlternativeMilestoneAmount ?? false,
                        turnover_1: "",
                        turnover_2: "",
                    };
                }
            });
    } else {
        return [];
    }
};

/** Dynamic style for AutocompleteFormComponents, used for renderInput props */
export const hasLegacyEan13 = (ean13?: string | null) => {
    const value = ean13?.trim() ?? "";
    return value.length > 0 && value !== "draft" && !value.startsWith("draft_");
};

export const hasLegacyReleaseDate = (releaseDate?: string | Date | null) =>
    releaseDate != null && `${releaseDate}`.trim().length > 0;

export const hasLegacyChannelName = (name?: string | null) => (name?.trim() ?? "").length > 0;

export const onHighlightField = (highlight: boolean) => {
    const fieldStyle = {
        "&.MuiTextField-root": {
            ".MuiOutlinedInput-root": {
                backgroundColor: highlight ? `${variables["info-light"]}` : "none",
                ".MuiOutlinedInput-notchedOutline": {
                    borderColor: highlight ? `${variables["info"]} !important` : "#EAEAEA",
                },
            },
        },
    };
    return fieldStyle;
};

export const mergePriceHistory = (
    overriddenChannelsValues: OverriddenChannel[],
    priceHistory: IPriceHistoryEntry[] = [],
    currentPeriod?: TPeriod,
): TOverriddenChannelsData[] => {
    return overriddenChannelsValues.map((o) => {
        const channelEntries = priceHistory.filter((p) => p.channelId === o.channelId && !o.countryMarket);
        // Legacy price/currency must come from THIS channel's entries: the one
        // covering the active period, else the open-ended one, else the first —
        // never from another channel, and never silently 0 when a period is
        // missing or not loaded.
        const activePriceHistory =
            channelEntries.find((e) => isPriceHistoryEntryConnectedToActivePeriod(e, currentPeriod)) ??
            channelEntries.find((e) => e.endDate === null) ??
            channelEntries[0];

        return {
            ...o,
            price: activePriceHistory?.price ?? 0,
            currency: activePriceHistory?.currency,
            priceHistory: channelEntries.map(({ id, ...priceHistory }) =>
                id < 0 ? priceHistory : { ...priceHistory, id },
            ),
        };
    });
};
